import { LowerCasePipe } from '@angular/common';
import { Component, OnInit, signal, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService, EnvironmentService, MainState, ModalService, TranslationService as PortalTranslationService, Role, TranslationPipe, User, UtilityService } from '@cloud-and-service/portal-lib';
import { Store } from '@ngxs/store';
import { LanguageSelectorComponent } from 'src/app/components/language-selector/language-selector.component';
import { RedirectType } from 'src/app/models/redirect-type';
import { CallBackService } from 'src/app/services/callback.service';
import { ConsumptionService } from 'src/app/services/consumption.service';
import { LocationService } from 'src/app/services/location.service';
import { TenantService } from 'src/app/services/tenant.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    FormsModule,
    LowerCasePipe,
    RouterLink,
    TranslationPipe,
    LanguageSelectorComponent,
  ],
})
export class LoginComponent implements OnInit {
  username: string | null = null;
  password: string | null = null;

  loginPageSettings: any;

  /** Only a tenant login (/<tenant>/login) whose portal allows it; never the general login page. */
  readonly showForgotPassword = signal(false);

  logo: Signal<string | null>;
  title: Signal<string | null>;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private store: Store,
    private callBackService: CallBackService,
    private utilityService: UtilityService,
    private locationService: LocationService,
    public consumptionService: ConsumptionService,
    private modalService: ModalService,
    private translation: PortalTranslationService,
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    environmentService: EnvironmentService
  ) {
    var environment = environmentService.getEnvironment();
    this.loginPageSettings = environment.loginPageSettings;
    this.logo = this.store.selectSignal(MainState.logo);
    this.title = this.store.selectSignal(MainState.title);
  }

  async ngOnInit(): Promise<void> {
    this.modalService.closeAllModals();

    const routePrefix = this.activatedRoute.snapshot.paramMap.get('forsyning');
    if (routePrefix) {
      this.showForgotPassword.set(await this.tenantService.isPasswordResetEnabled(routePrefix));
    }
  }

  forgotPasswordLink(): string[] {
    return ['/', this.activatedRoute.snapshot.paramMap.get('forsyning') ?? '', 'forgot-password'];
  }

  async login() {
    if (!this.username || !this.password) {
      return;
    }

    await this.authService.login(this.username, this.password);

    const auth = this.store.selectSnapshot(MainState.auth);
    if (!auth) {
      this.modalService.error(this.translation.getTranslation('ERROR_LOGIN'));
      return;
    }

    const _user: User = await this.userService.getCurrentUser();
    if (!this.store.selectSnapshot(MainState.user)) {
      this.modalService.error(this.translation.getTranslation('ERROR_CURRENT_USER'));
      return;
    }

    if (!this.store.selectSnapshot(MainState.translations)?.length) {
      return;
    }

    if (_user.role == Role.user) {
      await this.locationService.presentLocations(true);

      if (!this.utilityService.isEmpty(auth.urlRoute)) {
        this.router.navigate([`${auth.urlRoute}/home`]);
      } else {
        this.router.navigate(['/']);
      }
    } else if (_user.role == Role.admin) {
      this.router.navigate(['admin']);
    }
  }

  async getIdpService(service: string) {
    const uri: any = await this.callBackService.getIdpBroker(
      this.store.selectSnapshot(MainState.routePrefix),
      service,
      RedirectType.login
    );
    window.open(uri.url, '_self');
  }
}
